import { ApiResponse, FormStore, GlobalStore } from "@roc/feature-app-core";
import { DocumentStore } from "@roc/feature-documents";
import { LoanStore } from "@roc/feature-loans";
import { PayoffService } from "../services/payoffService";
import { action, computed, flow, makeObservable, observable } from "mobx";
import { format } from "date-fns";

const form = {
  fields: {
    loanId: {
      value: '',
      error: null,
      rule: '',
    },
    goodThroughDate: {
      value: '',
      error: null,
      rule: 'required',
    },
    requestedDate: {
      value: '',
      error: null,
      rule: '',
    },
    rush: {
      value: false,
      error: null,
      rule: '',
    },
    cema: {
      value: false,
      error: null,
      rule: '',
    },
    requesterEmail: {
      value: '',
      error: null,
      rule: '',
    },
    defaultRate: {
      value: '',
      error: null,
      rule: '',
    },
    comments: {
      value: '',
      error: null,
      rule: '',
    },
    foreclosure: {
      value: false,
      error: null,
      rule: '',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

export class PayoffRequestDetailStore extends FormStore {

  private documentStore: DocumentStore;
  private globalStore: GlobalStore;
  loanStore: LoanStore;
  payoffService: PayoffService;
  loanId: string;
  hasActivePayoff: boolean = false;

  constructor(
    loanStore: LoanStore,
    documentStore: DocumentStore,
    globalStore: GlobalStore
  ) {
    super({ ...form }, globalStore);
    this.documentStore = documentStore;
    this.globalStore = globalStore;
    this.loanStore = loanStore;
    this.payoffService = new PayoffService();

    makeObservable(this, {
      loanId: observable,
      setLoanId: action,
      getPayoffData: flow,
      submitPayoffRequestDetails: flow,
      hasActivePayoff: observable,
    });
  }

  initialize() {
    this.resetStore();
  }

  setLoanId(loanId: string) {
    this.loanId = loanId;
  }

  *getPayoffData(loanId: any, loanPayoffDataId: any) {
    let response;
    if (loanPayoffDataId) {
      response = yield this.payoffService.getPayoffById(loanId, loanPayoffDataId);
    } else {
      response = yield this.payoffService.getPayoffData(loanId);
    }
    const data = response.data.data;
    if (data?.payoffStatus !== 'Retracted' && data?.payoffStatus !== 'Expired')
      this.hasActivePayoff = true;

    if (this.hasActivePayoff) {
      this.onFieldChange('loanId', loanId);
      this.onFieldChange('goodThroughDate', data.goodThroughDate);
      this.onFieldChange('requestedDate', data.requestedDate);
      this.onFieldChange('rush', data.rush);
      this.onFieldChange('cema', data.cema);
      this.onFieldChange('requesterEmail', data.requesterEmail);
      this.onFieldChange('defaultRate', data.defaultRate);
      this.onFieldChange('comments', data.comments);
      this.onFieldChange('foreclosure', data.foreclosure);
    } else {
      this.onFieldChange('loanId', loanId);
      this.onFieldChange('requestedDate', Date.now());
      this.onFieldChange('rush', false);
      this.onFieldChange('cema', false);
      this.onFieldChange('foreclosure', false);
    }
  }

  *submitPayoffRequestDetails(isEdit, payoffId) {
    try {
      this.runFormValidationWithMessage();
      if (this.form.meta.isValid) {
        var response: ApiResponse;
        const data = this.getFormValues();
        if (isEdit) {
          response = yield this.payoffService.updatePayoffRequestDetails({
            ...data,
            loanId: this.loanId,
            goodThroughDate: data.goodThroughDate ? format(new Date(data.goodThroughDate), 'MM/dd/yyyy') : '',
            requestedDate: data.requestedDate ? format(new Date(data.requestedDate), 'MM/dd/yyyy') : format(Date.now(), 'MM/dd/yyyy'),
            comments: data.comments ? data.comments : '',
            rush: data.rush ? data.rush : false,
            requesterEmail: data.requesterEmail ? data.requesterEmail : '',
            needAssistance: false,
            foreclosure: data.foreclosure ? data.foreclosure : false,
            payoffId,
          });
        } else {
          response = yield this.payoffService.requestPayoff({
            ...data,
            loanId: this.loanId,
            goodThroughDate: data.goodThroughDate ? format(new Date(data.goodThroughDate), 'MM/dd/yyyy') : '',
            requestedDate: data.requestedDate ? format(new Date(data.requestedDate), 'MM/dd/yyyy') : format(Date.now(), 'MM/dd/yyyy'),
            comments: data.comments ? data.comments : '',
            rush: data.rush ? data.rush : false,
            requesterEmail: data.requesterEmail ? data.requesterEmail : '',
            needAssistance: false,
            foreclosure: data.foreclosure ? data.foreclosure : false,
          });
        }
        yield this.documentStore.documentFormStore.closeCurrentForm();
        yield this.loanStore.refreshLoanDetails();

        const dataResponse = JSON.parse(response.data?.data);
        if (dataResponse?.success) {
          this.globalStore.notificationStore.showSuccessNotification({
            message: 'Payoff Request Details submitted successfully',
          });
        } else {
          this.globalStore.notificationStore.showErrorNotification({
            message: dataResponse?.responseMessage,
          });
        }
      }
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while submitting payoff request details',
      });
    }
  }

  resetStore() {
    this.reset();
    this.loanId = null;
  }
}

export default PayoffRequestDetailStore;
